import React, { useState } from 'react';
import { css, cx } from '@emotion/css';
import { useForm } from 'react-hook-form';
import { z } from 'zod';
import { IoMail, IoNotifications, IoCheckmarkCircle, IoAlertCircle } from 'react-icons/io5';
import { useSubscribeToNotifications } from '../../hooks/notifications-api.hooks';
import { useAuth } from '../../../auth/hooks/use-auth';

// When authenticated, no email is required; for guests, email is required
type FormData = { email?: string };

export interface NewsletterSubscribeProps extends React.HTMLAttributes<HTMLDivElement> {}

const wrapper = css({
  width: '100%',
  display: 'flex',
  justifyContent: 'center',
  padding: '0 var(--spacing-md)',
  color: 'var(--pc-600)',
  margin: 'var(--spacing-4xl) 0',
  '@media (max-width: 768px)': {
    margin: 'var(--spacing-3xl) 0',
  },
  '@media (max-width: 480px)': {
    margin: 'var(--spacing-2xl) 0',
  },
});

const container = css({
  width: '100%',
  maxWidth: '1440px',
  position: 'relative',
  background: 'linear-gradient(135deg, rgba(var(--pcrgba-200), 0.6) 0%, rgba(var(--pcrgba-200), 0.4) 50%)',
  borderRadius: 'var(--spacing-lg)',
  boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)',
  overflow: 'hidden',
});

const content = css({
  display: 'flex',
  flexDirection: 'column',
  padding: 'var(--spacing-3xl)',
  width: '100%',
  zIndex: 2,
  alignItems: 'center',
  position: 'relative',
  '@media (max-width: 768px)': {
    padding: 'var(--spacing-xl)',
  },
  '@media (max-width: 480px)': {
    padding: 'var(--spacing-lg)',
  },
});

const info = css({
  maxWidth: '800px',
  marginBottom: 'var(--spacing-xl)',
  width: '100%',
  textAlign: 'center',
});

const iconCircle = css({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  width: '60px',
  height: '60px',
  backgroundColor: 'rgba(var(--pcrgba-600), 0.15)',
  borderRadius: '50%',
  margin: '0 auto var(--spacing-md)',
  color: 'var(--pc-500)',
  '@media (max-width: 768px)': {
    width: '50px',
    height: '50px',
  },
  '@media (max-width: 480px)': {
    width: '44px',
    height: '44px',
  },
});

const title = css({
  fontSize: 'var(--fs-700)',
  fontWeight: 700,
  marginBottom: 'var(--spacing-md)',
  lineHeight: 1.2,
  '@media (max-width: 768px)': {
    fontSize: 'var(--fs-600)',
  },
  '@media (max-width: 480px)': {
    fontSize: 'var(--fs-500)',
  },
});

const description = css({
  fontSize: 'var(--fs-450)',
  color: 'rgba(var(--pcrgba-600), 0.9)',
  lineHeight: 1.6,
  marginBottom: 'var(--spacing-lg)',
  '@media (max-width: 768px)': {
    fontSize: 'var(--fs-400)',
  },
  '@media (max-width: 480px)': {
    fontSize: 'var(--fs-350)',
  },
});

const form = css({
  display: 'flex',
  flexDirection: 'column',
  gap: 'var(--spacing-md)',
  maxWidth: '800px',
  width: '100%',
});

const formGroup = css({
  display: 'flex',
  flexDirection: 'column',
  gap: 'var(--spacing-2xs)',
});

const inputGroup = css({
  display: 'flex',
  gap: 'var(--spacing-md)',
  alignItems: 'center',
  '@media (max-width: 768px)': {
    flexDirection: 'column',
  },
});

const inputWrapper = css({
  flex: 1,
  '@media (max-width: 768px)': {
    width: '100%',
  },
});

const label = css({
  fontSize: 'var(--fs-350)',
  fontWeight: 600,
  color: 'var(--pc-500)',
  marginBottom: 'var(--spacing-2xs)',
  display: 'block',
});

const input = css({
  padding: '0 var(--spacing-md)',
  borderRadius: 'var(--spacing-sm)',
  fontSize: 'var(--fs-400)',
  border: 'none',
  backgroundColor: 'rgba(var(--primary-color-rgb), 0.95)',
  height: '52px',
  outline: 'none',
  transition: 'box-shadow 0.2s',
  width: '100%',
  '&:focus': {
    boxShadow: '0 0 0 2px var(--pc-500)',
  },
  '@media (max-width: 480px)': {
    height: '48px',
    fontSize: 'var(--fs-350)',
  },
});

const errorText = css({
  fontSize: 'var(--fs-300)',
  color: '#ef4444',
  marginTop: 'var(--spacing-2xs)',
});

const button = css({
  minWidth: '150px',
  height: '52px',
  padding: '0 var(--spacing-lg)',
  borderRadius: 'var(--spacing-sm)',
  fontSize: 'var(--fs-400)',
  backgroundColor: 'var(--pc-600)',
  color: 'var(--pc-100)',
  border: 'none',
  fontWeight: 600,
  cursor: 'pointer',
  transition: 'all 0.2s ease-in-out',
  boxShadow: '0 2px 4px rgba(0, 0, 0, 0.1)',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  whiteSpace: 'nowrap',
  '&:hover:not(:disabled)': {
    backgroundColor: 'var(--pc-500)',
    transform: 'translateY(-2px)',
    boxShadow: '0 4px 8px rgba(0, 0, 0, 0.15)',
  },
  '&:disabled': {
    opacity: 0.6,
    cursor: 'not-allowed',
  },
  '@media (max-width: 768px)': {
    width: '100%',
  },
  '@media (max-width: 480px)': {
    height: '48px',
    fontSize: 'var(--fs-350)',
  },
});

// Auth-only layout tweaks
const authGroup = css({
  width: '100%',
  maxWidth: '560px',
  margin: '0 auto',
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'stretch',
});

const buttonFullWidth = css({
  width: '100%',
});

const helperText = css({
  textAlign: 'center',
  color: 'rgba(var(--pcrgba-600), 0.9)',
  fontSize: 'var(--fs-350)',
  marginBottom: 'var(--spacing-sm)',
});

const message = css({
  display: 'flex',
  alignItems: 'center',
  gap: 'var(--spacing-xs)',
  padding: 'var(--spacing-sm) var(--spacing-md)',
  borderRadius: 'var(--spacing-sm)',
  fontSize: 'var(--fs-350)',
});

const messageSuccess = css({
  backgroundColor: 'rgba(167, 243, 208, 0.2)',
  color: '#10b981',
});

const messageError = css({
  backgroundColor: 'rgba(254, 202, 202, 0.2)',
  color: '#ef4444',
});

const features = css({
  display: 'flex',
  gap: 'var(--spacing-md)',
  marginTop: 'var(--spacing-md)',
  '@media (max-width: 768px)': {
    flexDirection: 'column',
    alignItems: 'flex-start',
  },
});

const feature = css({
  display: 'flex',
  alignItems: 'center',
  gap: 'var(--spacing-xs)',
  color: 'rgba(var(--pcrgba-600), 0.85)',
  fontSize: 'var(--fs-350)',
});

const decoration = css({
  position: 'absolute',
  top: 0,
  right: 0,
  bottom: 0,
  left: 0,
  zIndex: 1,
  overflow: 'hidden',
  pointerEvents: 'none',
});

const decorationCircle = css({
  position: 'absolute',
  borderRadius: '50%',
  background: 'radial-gradient(circle, rgba(var(--primary-color-rgb), 0.1) 0%, rgba(var(--primary-color-rgb), 0) 70%)',
  '&:nth-child(1)': {
    width: '500px',
    height: '500px',
    top: '-250px',
    right: '-150px',
  },
  '&:nth-child(2)': {
    width: '400px',
    height: '400px',
    bottom: '-200px',
    left: '-100px',
  },
});

export const NewsletterSubscribe: React.FC<NewsletterSubscribeProps> = ({
  className,
  style,
  ...rest
}) => {
  const [statusMessage, setStatusMessage] = useState<string | null>(null);
  const [status, setStatus] = useState<'success' | 'error' | null>(null);

  const { mutate: subscribe, isPending } = useSubscribeToNotifications();
  const { isAuthenticated, user, isLoading } = useAuth();

  // Build schema based on auth state: guests must provide email; logged-in users don't
  const guestSchema: z.ZodType<FormData> = z.object({
    email: z.string().email('O E-mail digitado é inválido.'),
  });

  const {
    register,
    handleSubmit,
    reset,
    setError,
    formState: { errors },
  } = useForm<FormData>({
  });

  const onSubmit = async (data: FormData) => {
    if (!isAuthenticated) {
      const parsed = guestSchema.safeParse(data);
      if (!parsed.success) {
        const msg = parsed.error.issues[0]?.message || 'O E-mail digitado é inválido.';
        setError('email', { type: 'validate', message: msg });
        return;
      }
    }

    const payload = isAuthenticated ? undefined : { email: data.email! };
    subscribe(
      payload as any,
      {
        onSuccess: (response) => {
          setStatus('success');
          setStatusMessage(response.message || 'Obrigado por se inscrever na nossa newsletter!');
          reset();
          setTimeout(() => {
            setStatus(null);
            setStatusMessage(null);
          }, 5000);
        },
        onError: (error: any) => {
          setStatus('error');
          const errorMessage = error?.response?.data?.message || 'Houve um erro ao processar seu pedido. Tente novamente.';
          setStatusMessage(errorMessage);
          console.error('There was an error processing the request', error);
        },
      }
    );
  };

  return (
    <div className={cx(wrapper, className)} style={style} {...rest}>
      <div className={container}>
        <div className={decoration}>
          <div className={decorationCircle} />
          <div className={decorationCircle} />
        </div>
        <div className={content}>
          <div className={info}>
            <div className={iconCircle}>
              <IoMail size={24} />
            </div>
            <h2 className={title}>Fique por dentro das novidades</h2>
            <p className={description}>
              Assine nossa newsletter e receba atualizações sobre eventos, promoções e ofertas
              exclusivas diretamente no seu e-mail.
            </p>
          </div>

          <form className={form} onSubmit={handleSubmit(onSubmit)}>
            <div className={formGroup}>
              {!isAuthenticated && <label className={label}>Email</label>}

              {isAuthenticated && (
                <div className={helperText}>
                  Inscrição será vinculada à sua conta{user?.email ? ` (${user.email})` : ''}.
                </div>
              )}

              <div className={cx(inputGroup, isAuthenticated && authGroup)}>
                {!isAuthenticated && (
                  <div className={inputWrapper}>
                    <input
                      {...register('email')}
                      className={input}
                      placeholder="Digite seu e-mail..."
                      type="email"
                      disabled={isPending || isLoading}
                    />
                  </div>
                )}
                <button className={cx(button, isAuthenticated && buttonFullWidth)} disabled={isPending || isLoading} type="submit">
                  {isPending ? 'Enviando...' : 'Inscrever-se'}
                </button>
              </div>

              {!isAuthenticated && errors.email && <span className={errorText}>{errors.email.message}</span>}
            </div>

            {status && statusMessage && (
              <div className={`${message} ${status === 'success' ? messageSuccess : messageError}`}>
                {status === 'success' ? (
                  <IoCheckmarkCircle size={18} />
                ) : (
                  <IoAlertCircle size={18} />
                )}
                <p>{statusMessage}</p>
              </div>
            )}

            <div className={features}>
              <div className={feature}>
                <IoNotifications size={16} />
                <span>Alertas de novos eventos</span>
              </div>
              <div className={feature}>
                <IoCheckmarkCircle size={16} />
                <span>Promoções exclusivas</span>
              </div>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default NewsletterSubscribe;
